// Events query layer. No 'use client' directive — server pages import the
// keys/fetchers for prefetch wiring; client components call the hooks.

import { useMemo } from 'react';
import {
  keepPreviousData,
  useQuery,
  useInfiniteQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';

export type EventSummary = components['schemas']['EventSummary'];
export type EventDetail = components['schemas']['EventDetail'];
export type RsvpView = components['schemas']['RsvpView'];
export type UpcomingMeetingView = components['schemas']['UpcomingMeetingView'];
export type UpcomingMeetingsResponse = components['schemas']['UpcomingMeetingsResponse'];

export type ListEventsInput = {
  scope?: 'mine' | 'circle' | 'network' | 'platform';
  window?: 'upcoming' | 'past';
  pageSize?: number;
  cursor?: string;
  from?: string;
  to?: string;
  placeText?: string;
  placeCountryCode?: string;
  tag?: string;
  q?: string;
  groupId?: string;
  format?: 'in_person' | 'virtual';
};

export type EventsPage = {
  items: EventSummary[];
  nextCursor: string | null;
  pageSize: number;
};

// ---- Query keys ----

export const eventsKeys = {
  all: ['events'] as const,
  lists: () => [...eventsKeys.all, 'list'] as const,
  list: (params: ListEventsInput) =>
    [...eventsKeys.lists(), params] as const,
  // Keyed under lists() so useRsvpEvent.onSettled invalidation reaches the rail.
  myUpcomingMeetings: (pageSize: number) =>
    [...eventsKeys.lists(), 'my-upcoming-meetings', { pageSize }] as const,
  detail: (id: string) => [...eventsKeys.all, 'detail', id] as const,
  attendees: (id: string, limit?: number) =>
    [...eventsKeys.all, 'attendees', id, { limit }] as const,
  myCircleOptions: () =>
    [...eventsKeys.all, 'my-circle-options'] as const,
};

// ---- Client fetchers ----

export async function fetchEvents(
  input: ListEventsInput = {},
): Promise<EventsPage> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/events', {
    params: {
      query: {
        scope: input.scope,
        window: input.window,
        pageSize: input.pageSize,
        cursor: input.cursor,
        from: input.from,
        to: input.to,
        placeText: input.placeText,
        placeCountryCode: input.placeCountryCode,
        tag: input.tag,
        q: input.q,
        groupId: input.groupId,
        format: input.format,
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchEventDetail(id: string): Promise<EventDetail> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/events/{id}', {
    params: { path: { id } },
  });
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export async function fetchEventAttendees(
  id: string,
  limit = 6,
): Promise<readonly RsvpView[]> {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/events/{id}/attendees',
    {
      params: { path: { id }, query: { pageSize: limit } },
    },
  );
  if (data === undefined) {
    // Non-organizers get 403 — return empty preview rather than throwing,
    // mirroring the server-side `getEventAttendeePreview` tolerance.
    if (response.status === 403 || response.status === 404) return [];
    throwApiError(error);
  }
  return data.items;
}

// ---- Hooks ----

export function useEvents(
  input: ListEventsInput,
  options?: Omit<
    UseQueryOptions<EventsPage, Error, EventsPage>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: eventsKeys.list(input),
    queryFn: () => fetchEvents(input),
    ...options,
  });
}

export function useInfiniteEvents(input: Omit<ListEventsInput, 'cursor'>) {
  return useInfiniteQuery({
    queryKey: eventsKeys.list(input),
    queryFn: ({ pageParam }) =>
      fetchEvents({ ...input, cursor: pageParam ?? undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

// ---- URL search → list input (single source of truth) ----
//
// Shape of the events index URL. Tolerates partials/raw strings so the route's
// `validateSearch` output and the body's `useFilterParams` `values` (which
// already narrows to known enums) are both assignable.

export type EventsListFormat = 'in_person' | 'virtual';
export type EventsListTab = 'mine' | 'circle' | 'network' | 'platform';

export type EventsSearchInput = {
  q?: string;
  tab?: EventsListTab;
  when?: 'upcoming' | 'past';
  format?: EventsListFormat | 'any';
  from?: string;
  to?: string;
};

const EVENTS_DEFAULT_PAGE_SIZE = 25;

function eventDateToIsoStart(d: string): string | undefined {
  const parsed = new Date(`${d}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

function eventDateToIsoEnd(d: string): string | undefined {
  const parsed = new Date(`${d}T23:59:59Z`);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

export function eventsListInputFromSearch(
  search: EventsSearchInput,
): Omit<ListEventsInput, 'cursor'> {
  const tab: EventsListTab = search.tab ?? 'mine';
  const q = (search.q ?? '').trim();
  const format: EventsListFormat | 'any' = search.format ?? 'any';
  const from = search.from ?? '';
  const to = search.to ?? '';
  const fromIso = from === '' ? undefined : eventDateToIsoStart(from);
  const toIso = to === '' ? undefined : eventDateToIsoEnd(to);
  return {
    scope: tab,
    pageSize: EVENTS_DEFAULT_PAGE_SIZE,
    ...(search.when === 'past' ? { window: 'past' as const } : {}),
    ...(q.length > 0 ? { q } : {}),
    ...(format !== 'any' ? { format } : {}),
    ...(fromIso !== undefined ? { from: fromIso } : {}),
    ...(toIso !== undefined ? { to: toIso } : {}),
  };
}

export function useInfiniteEventsFromSearch(search: EventsSearchInput) {
  const input = useMemo(() => eventsListInputFromSearch(search), [search]);
  return useInfiniteEvents(input);
}

export function useEventDetail(
  id: string,
  options?: Omit<
    UseQueryOptions<EventDetail, Error, EventDetail>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: eventsKeys.detail(id),
    queryFn: () => fetchEventDetail(id),
    ...options,
  });
}

export function useEventAttendees(id: string, limit?: number) {
  return useQuery({
    queryKey: eventsKeys.attendees(id, limit),
    queryFn: () => fetchEventAttendees(id, limit),
  });
}

// ---- My upcoming meetings (feed rail) ----

// Rail shows up to 3 (UpcomingEventsSection cap). One extra so the section
// can detect overflow and surface the "View all events →" footer.
const RAIL_PAGE_SIZE = 4;

export async function fetchMyUpcomingMeetings(
  pageSize = RAIL_PAGE_SIZE,
): Promise<UpcomingMeetingsResponse> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/members/me/meetings', {
    params: { query: { pageSize } },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export function useMyUpcomingMeetings(pageSize = RAIL_PAGE_SIZE) {
  return useQuery({
    queryKey: eventsKeys.myUpcomingMeetings(pageSize),
    queryFn: () => fetchMyUpcomingMeetings(pageSize),
  });
}
